import '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import { morph } from '@theme/morph';

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('#PredictiveSearchResults');
    this.reset = this.querySelector('[data-predictive-search-reset]');
    this.status = this.querySelector('[data-predictive-search-status]');
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.input?.addEventListener('input', this.onInput, { signal });
    this.input?.addEventListener('keydown', this.onKeydown, { signal });
    this.reset?.addEventListener('click', this.clear, { signal });
    this.closest('modal-dialog')?.addEventListener('dialog:close', this.onDialogClose, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
    if (this.timer) clearTimeout(this.timer);
  }

  onInput = () => {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(), 180);
  };

  async search() {
    const query = this.input?.value.trim();
    if (!query) {
      this.clearResults();
      return;
    }

    this.requestController?.abort();
    this.requestController = new AbortController();
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));
    this.setAttribute('aria-busy', 'true');
    this.reset.hidden = false;

    try {
      const url = new URL(document.documentElement.dataset.predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: this.requestController.signal });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.querySelector('#PredictiveSearchResults');
      if (!next || !this.results) throw new Error('Search results unavailable');
      morph(this.results, next);
      this.activeIndex = -1;
      const count = Number(this.results.dataset.resultCount) || 0;
      this.input?.setAttribute('aria-expanded', String(this.items.length > 0));
      if (this.status) {
        this.status.textContent = count > 0
          ? (this.dataset.resultsMessage || '').replace('__COUNT__', String(count))
          : this.dataset.noResultsMessage || '';
      }
      deferred.resolve({ totalCount: count });
    } catch (error) {
      deferred.reject(error);
      if (error.name !== 'AbortError') {
        this.results?.setAttribute('data-search-error', 'true');
        if (this.status) this.status.textContent = this.dataset.errorMessage || '';
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  get items() {
    return [...(this.results?.querySelectorAll('[data-predictive-search-item]') || [])];
  }

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.onDialogClose();
      this.closest('modal-dialog')?.close();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;

    const items = this.items;
    if (!items.length) return;
    if (event.key === 'Enter' && this.activeIndex < 0) return;
    event.preventDefault();

    if (event.key === 'ArrowDown') this.activeIndex = (this.activeIndex + 1) % items.length;
    if (event.key === 'ArrowUp') this.activeIndex = (this.activeIndex - 1 + items.length) % items.length;
    if (event.key === 'Enter') {
      items[this.activeIndex]?.click();
      return;
    }

    for (const [index, item] of items.entries()) item.setAttribute('aria-selected', String(index === this.activeIndex));
    const active = items[this.activeIndex];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  };

  clear = () => {
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.clearResults();
  };

  clearResults() {
    this.requestController?.abort();
    this.activeIndex = -1;
    this.input?.removeAttribute('aria-activedescendant');
    this.input?.setAttribute('aria-expanded', 'false');
    if (this.reset) this.reset.hidden = true;
    this.results?.replaceChildren();
    if (this.status) this.status.textContent = '';
  }

  onDialogClose = () => {
    this.activeIndex = -1;
    this.input?.removeAttribute('aria-activedescendant');
    this.input?.setAttribute('aria-expanded', 'false');
    for (const item of this.items) item.setAttribute('aria-selected', 'false');
  };
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
